import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import type { DocumentTemplateType } from '../models/Document';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../models/User';

export function useDocumentTemplates(organizationId?: string | null) {
  const [templates, setTemplates] = useState<DocumentTemplateType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    setLoading(true);

    let q;
    if (organizationId) {
      // Filter by organization - query without orderBy to avoid index requirement
      // We'll sort in JavaScript instead
      q = query(
        collection(db, 'templates'),
        where('organizationId', '==', organizationId)
      );
    } else if (currentUser?.role === UserRole.SUPER_ADMIN) {
      // SUPER_ADMIN can see all templates - can use orderBy since no where clause
      q = query(
        collection(db, 'templates'),
        orderBy('createdAt', 'desc')
      );
    } else if (currentUser?.organizationId) {
      // Filter by user's organization - query without orderBy to avoid index requirement
      q = query(
        collection(db, 'templates'),
        where('organizationId', '==', currentUser.organizationId)
      );
    } else {
      // No organization - return empty
      setTemplates([]);
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const templatesData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as DocumentTemplateType;
        });

        // Always sort by createdAt descending (newest first)
        // This works immediately without waiting for index to build
        templatesData.sort((a, b) => {
          const aTime = a.createdAt.getTime();
          const bTime = b.createdAt.getTime();
          return bTime - aTime; // Descending order
        });

        setTemplates(templatesData);
        setLoading(false);
        setError(null);
      },
      (err: any) => {
        console.error('Error fetching document templates:', err);
        // Check if it's an index building error
        const errorMessage = err?.message || '';
        if (err?.code === 'failed-precondition' && (errorMessage.includes('index') || errorMessage.includes('Index'))) {
          setError(new Error('Database indexes are currently building. Please wait a few minutes and refresh the page.'));
        } else {
          setError(err as Error);
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [organizationId, currentUser?.role, currentUser?.organizationId]);

  const createTemplate = async (templateData: Omit<DocumentTemplateType, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const docRef = await addDoc(collection(db, 'templates'), {
        ...templateData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating document template:', err);
      throw err;
    }
  };

  const updateTemplate = async (templateId: string, updates: Partial<DocumentTemplateType>) => {
    try {
      const templateRef = doc(db, 'templates', templateId);
      await updateDoc(templateRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      });
    } catch (err) {
      console.error('Error updating document template:', err);
      throw err;
    }
  };

  const deleteTemplate = async (templateId: string) => {
    try {
      const templateRef = doc(db, 'templates', templateId);
      await deleteDoc(templateRef);
    } catch (err) {
      console.error('Error deleting document template:', err);
      throw err;
    }
  };

  return {
    templates,
    loading,
    error,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  };
}
